import {
  ALL_CHART_TYPES,
  DEFAULT_NORMALIZE_BY,
  DEFAULT_NORMALIZE_BY_SETTING
} from "constants/chart.constants";
import { EvaSource, TimeStep } from "constants/charts.enums";
import { FIELD_NAMES } from "constants/fieldLabels.constants";
import { createMergeWithDefaults } from "entities/utils";
import { ProbitChartRequestType, ProbitChartSettings } from "types";

import { MaxBinsSortOrderEnum } from "models/binSize";
import { EntityKind } from "models/entityKind";

import { PdenSourceEnum } from "../../../../models/pdenDataSourceSetting";
import { BASE_CHART_REQUEST } from "./defaults";

export const DEFAULT_PROBIT_SETTINGS: Partial<ProbitChartSettings> = {
  product: "Oil",
  chartType: ALL_CHART_TYPES.Probit.id,
  showForecast: false,
  pdenDataSourceSetting: {
    source: EvaSource.Public,
    timeStep: TimeStep.Month
  },
  showLinesOfBestFit: true,
  showZeros: false,
  forecastMode: "forecastTrump",
  useForecastStartDate: false,
  survivorBias: true,
  rateType: 0,
  cutoff: 50,
  timeStep: TimeStep.Month,
  shutInMonths: 3,
  source: EvaSource.Public,
  normalizeBy: DEFAULT_NORMALIZE_BY,
  useWeightedRatioAverage: true,
  xVariable: {
    valueProperty: "Performance.Oil.TwelveMCalTimeCum.fcst",
    displayTitle: "12M CalTime Cum + Forecast (Mbbl)",
    canBin: true,
    dataType: "Number",
    mappingName: "Performance.Oil.TwelveMCalTimeCum.fcst",
    bin: null,
    pdenSource: PdenSourceEnum.Public.toString()
  },
  yVariable: {
    valueProperty: "Well_Design.HzLength_m",
    displayTitle: FIELD_NAMES.HZ_LENGTH,
    canBin: true,
    dataType: "Number",
    mappingName: "Well_Design.HzLength_m",
    pdenSource: PdenSourceEnum.Public.toString()
  },
  lockUnits: false,
  movingAverageDays: null
};

export const DEFAULT_PROBIT_REQUEST: Partial<ProbitChartRequestType> = {
  ...BASE_CHART_REQUEST,
  FilterId: "",
  requestId: "",
  ReverseColor: false,
  ColorPalette: {
    id: "-6",
    name: "Portland",
    shared: false,
    colors: [],
    preferredColors: [],
    paletteType: 0,
    colorScale: [],
    specialColors: null,
    thickness: [2],
    reverse: false
  },
  GroupBy: {
    title: "Resource Play",
    property: "Header.ResourcePlay",
    groupByField: "Header.ResourcePlay",
    pdenSource: "Public",
    canBin: false,
    dataType: "Text", // TODO: check this
    categoryId: 1,
    tooltip: "",
    display: "",
    entityKind: EntityKind.Well,
    bin: {
      BinSize: 0,
      MinSize: null,
      MaxBins: null,
      MaxBinsSortOrder: MaxBinsSortOrderEnum.WellCount,
      GreaterThan: null,
      LessThan: null,
      Quantile: {
        quantileType: "NumberOfQuantile",
        numQuantiles: 4
      },
      BinType: "BinSize",
      UseDynamicBins: false
    },
    mode: undefined,
    // Declared so createMergeWithDefaults keeps it: getAllowedKeys builds its allow-list from
    // this default object, and any key absent here is silently dropped from the request. Without
    // it a chart on Forecast Trumping sent a trumping-qualified property with no settings, and
    // the backend only runs the per-well source fallthrough when the settings are present.
    forecastTrumpingSettings: undefined,
    performanceDateRange: undefined
  },
  sortBy: "AlphabeticalAsc",
  userForecast: [],
  userForecastSettings: {
    forecastSourceFolder: null,
    excludeMcDanielForecasts: false,
    reserveCategory: null,
    excludeNoviForecasts: true,
    specialForecastFolders: ["McDaniel Research"]
  },
  forecastMode: "forecastTrump",
  useForecastStartDate: false,
  showGroupsNotInFilter: true,
  placeholderBinsEnabled: false,
  hasCheckedBins: true,
  syncClientPden: false,
  syncClientSchema: "",
  colorLockedItems: {},
  syncClientFcst: false,
  syncForecastRescat: "2111",
  normalizeBySetting: DEFAULT_NORMALIZE_BY_SETTING,
  chartSetting: DEFAULT_PROBIT_SETTINGS as ProbitChartSettings,
  chartFieldInformation: {
    xField: {
      type: "x",
      product: "Oil",
      defaultUnit: "Mbbl",
      title: "12M CalTime Cum + Forecast (Mbbl)",
      property: "Performance.Oil.TwelveMCalTimeCum.fcst",
      folderName: "",
      isForecast: true,
      pdenSource: PdenSourceEnum.Public,
      performanceDateRange: undefined
    },
    yField: {
      type: "y",
      title: FIELD_NAMES.HZ_LENGTH,
      property: "Well_Design.HzLength_m",
      folderName: "",
      performanceDateRange: undefined
    },
    groupByField: {
      type: "groupBy",
      title: "Resource Play",
      property: "Header.ResourcePlay",
      folderName: "",
      isForecast: false,
      performanceDateRange: undefined
    }
  },
  axisMinMax: {
    xMin: null,
    xMax: null,
    yMin: null,
    yMax: null
  },
  useProbitDataFromLineOfBestFit: false,
  isDefaultLockOn: true
};

export const generateProbitRequestBody = createMergeWithDefaults(DEFAULT_PROBIT_REQUEST);
